import { useEffect, useState } from 'react'
import { Leaf, LockKeyhole, ShieldCheck } from 'lucide-react'
import { botanicalSchema, supabase } from '../lib/supabase.js'
import Button from '../components/ui/Button.jsx'

export function FullScreenState({ title, description, children, loading = false }) {
  return (
    <main className="center-shell">
      <section className="message-card" aria-live={loading ? 'polite' : undefined}>
        <span className="brand-symbol"><Leaf aria-hidden="true" /></span>
        {loading && <span className="loading-orbit" aria-hidden="true" />}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
        {children}
      </section>
    </main>
  )
}

export function MissingConfiguration() {
  return (
    <FullScreenState title="Supabase configuration required">
      <p>Copy <code>.env.example</code> to <code>.env.local</code>, then provide the shared Supabase project URL and its publishable key. Never place a service-role key in this dashboard.</p>
    </FullScreenState>
  )
}

export function MembershipRequired({ session, error, onSignOut }) {
  return (
    <FullScreenState title="Botanical membership required" description={error || undefined}>
      <p>The account <strong>{session.user.email}</strong> is authenticated, but does not currently have active access to the <code>{botanicalSchema}</code> application schema.</p>
      <p className="supporting-copy">The first administrator can use <code>supabase/bootstrap_first_admin.sql</code>. Future access is granted through Botanical membership administration.</p>
      <Button variant="secondary" onClick={onSignOut}>Sign out</Button>
    </FullScreenState>
  )
}

export default function AuthPage({ initialError = '' }) {
  const [mode, setMode] = useState('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [message, setMessage] = useState(initialError)
  const [messageTone, setMessageTone] = useState('error')
  const [busy, setBusy] = useState(false)

  useEffect(() => { if (initialError) setMessage(initialError) }, [initialError])

  const submit = async (event) => {
    event.preventDefault()
    setBusy(true)
    setMessage('')
    try {
      const result = mode === 'signin'
        ? await supabase.auth.signInWithPassword({ email, password })
        : await supabase.auth.signUp({ email, password, options: { data: { full_name: displayName } } })
      if (result.error) {
        setMessageTone('error')
        setMessage(result.error.message)
      } else if (mode === 'signup' && !result.data.session) {
        setMessageTone('success')
        setMessage('Account created. Check your email to confirm the address, then sign in.')
      }
    } catch (error) {
      setMessageTone('error')
      setMessage(error instanceof Error ? error.message : 'Authentication could not be completed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-intro" aria-labelledby="product-title">
        <span className="auth-brand"><Leaf aria-hidden="true" /> Botanical Field Assistant</span>
        <div>
          <span className="eyebrow eyebrow-light">Knowledge infrastructure</span>
          <h1 id="product-title">Botanical knowledge, curated with care.</h1>
          <p>Build a transparent, source-linked reference platform for adaptive field identification and habitat interpretation.</p>
        </div>
        <ul className="principle-list">
          <li><ShieldCheck aria-hidden="true" /><span><strong>Human verified</strong>Controlled review keeps every approved record accountable.</span></li>
          <li><LockKeyhole aria-hidden="true" /><span><strong>Access governed</strong>Botanical roles and membership protect the curation workspace.</span></li>
        </ul>
      </section>

      <section className="auth-form-region">
        <form className="auth-card" onSubmit={submit}>
          <div className="segmented" aria-label="Authentication mode">
            <button type="button" className={mode === 'signin' ? 'active' : ''} aria-pressed={mode === 'signin'} onClick={() => { setMode('signin'); setMessage('') }}>Sign in</button>
            <button type="button" className={mode === 'signup' ? 'active' : ''} aria-pressed={mode === 'signup'} onClick={() => { setMode('signup'); setMessage('') }}>Create account</button>
          </div>
          <div className="auth-card-heading"><span className="eyebrow">Secure workspace</span><h2>{mode === 'signin' ? 'Welcome back' : 'Create your account'}</h2><p>{mode === 'signin' ? 'Sign in to continue to the curation dashboard.' : 'Membership approval may be required after registration.'}</p></div>
          {mode === 'signup' && <label>Display name<input autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></label>}
          <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          <label>Password<input type="password" minLength="8" autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
          {message && <div className={`form-notice notice-${messageTone}`} role={messageTone === 'error' ? 'alert' : 'status'}>{message}</div>}
          <Button type="submit" loading={busy} className="button-full">{mode === 'signin' ? 'Sign in' : 'Create account'}</Button>
          <p className="auth-security-note">This dashboard accepts only the project URL and publishable key in frontend configuration.</p>
        </form>
      </section>
    </main>
  )
}
